// Pure helpers for the Season editor (list rows, bulk values, template bodies).
// Only depends on `dayjs` (a plain npm module) so it runs under `node --test`.
const dayjs = require('dayjs');

const SEASON_TABS = Object.freeze({
  PRE_SEASON: 'pre_season',
  ACTIVE: 'active',
  ARCHIVE: 'archive',
});

const TAB_STATE = Object.freeze({
  [SEASON_TABS.PRE_SEASON]: 'template',
  [SEASON_TABS.ACTIVE]: 'active',
  [SEASON_TABS.ARCHIVE]: 'archived',
});

// Sentinel shown as "Multiple values" in bulk edit.
const MULTIPLE_VALUES = '__multiple_values__';

const CROP_KEYS = [
  'crop_type',
  'crop_variety',
  'crop_feature',
  'sow_date',
  'irrigation_start_date',
  'harvest_date',
];
// `recommendation_display` follows `irrigation_type`: a type change resets it
// to the type's default first (as Edit Field does), then an explicit choice
// applies.
const IRRIGATION_KEYS = [
  'irrigation_type',
  'irrigation_rate',
  'recommendation_display',
  'tank_type',
  'irrigation_hin',
];
const SETTINGS_KEYS = ['is_measured_rate_active'];
const FORM_KEYS = [
  'soil_type',
  'farming_type',
  ...CROP_KEYS,
  ...IRRIGATION_KEYS,
  ...SETTINGS_KEYS,
  'cluster',
  'area_size',
  'expiration_date',
];

const readSeasonValue = (season, key) => {
  if (CROP_KEYS.includes(key)) {
    const value = season?.crop_data?.[key] ?? '';
    return key === 'crop_type' ? String(value).toLowerCase() : value;
  }
  if (IRRIGATION_KEYS.includes(key)) {
    return season?.irrigation_system?.[key] ?? '';
  }
  if (SETTINGS_KEYS.includes(key)) {
    return Boolean(season?.settings?.[key]);
  }
  switch (key) {
    case 'cluster':
      return season?.cluster?.id ?? '';
    case 'area_size':
      // Unset (or 0): Edit Field shows the polygon's area instead.
      return season?.geo_data?.area_size > 0 ? season.geo_data.area_size : '';
    case 'expiration_date':
      return season?.contract_data?.expiration_date ?? null;
    default:
      return season?.[key] ?? '';
  }
};

// {key: value} where every season agrees, MULTIPLE_VALUES where they differ.
const commonValues = (seasons) => {
  const values = {};
  FORM_KEYS.forEach((key) => {
    const all = seasons.map((season) => readSeasonValue(season, key));
    values[key] = all.every((value) => value === all[0])
      ? all[0]
      : MULTIPLE_VALUES;
  });
  return values;
};

const seasonRowKey = (season) => season.id;

const buildRow = (season, block, fieldGroup) => ({
  key: seasonRowKey(season),
  season,
  block,
  fieldGroup,
});

const rowsForBlock = (block, fieldGroup, state) =>
  (block.seasons || [])
    .filter((season) => season.state === state)
    .map((season) => buildRow(season, block, fieldGroup));

// The season a new pre-season copies: the block's active season, else its
// newest archived one (the tree lists seasons newest sow date first).
const preSeasonSourceOf = (block) => {
  const seasons = block.seasons || [];
  return (
    seasons.find((season) => season.state === 'active') ||
    seasons.find((season) => season.state === 'archived') ||
    null
  );
};

const NEW_PRE_SEASON_PREFIX = 'new_pre_season:';

// Pre season tab: every block. A block with a pre-season shows it; a block
// without one shows a copy of its current season, created on "Save".
const preSeasonRowsForBlock = (block, fieldGroup) => {
  const rows = rowsForBlock(block, fieldGroup, 'template');
  if (rows.length > 0) {
    return rows;
  }
  const source = preSeasonSourceOf(block);
  const season = {
    ...(source || {}),
    id: NEW_PRE_SEASON_PREFIX + block.id,
    state: 'template',
    name: block.name,
  };
  return [{ ...buildRow(season, block, fieldGroup), isNewPreSeason: true }];
};

const DAY_SECONDS = 24 * 60 * 60;
const YEAR_SECONDS = 365 * DAY_SECONDS;

// "Activate now": the new season is installed today; its contract end is the
// pre-season's when still ahead, else a year from today (V1 Add Field's
// default).
const activationContractDates = (contractData, nowUnix) => {
  const expiration = Number(contractData?.expiration_date);
  return {
    installation_date: nowUnix,
    expiration_date: expiration > nowUnix ? expiration : nowUnix + YEAR_SECONDS,
  };
};

// The field header is shown for multi-block fields, and for a single-block
// field only when the field's name differs from its block's.
const shouldShowFieldHeader = (fieldGroup) => {
  const blocks = fieldGroup.blocks || [];
  return blocks.length !== 1 || blocks[0].name !== fieldGroup.name;
};

// [{fieldGroup, showHeader, rows}] for one tab; groups with no rows are dropped.
const buildSeasonGroups = (tree, tab) => {
  const state = TAB_STATE[tab];
  return (tree?.field_groups || [])
    .map((fieldGroup) => ({
      fieldGroup,
      showHeader: shouldShowFieldHeader(fieldGroup),
      rows: (fieldGroup.blocks || []).reduce(
        (rows, block) =>
          rows.concat(
            tab === SEASON_TABS.PRE_SEASON
              ? preSeasonRowsForBlock(block, fieldGroup)
              : rowsForBlock(block, fieldGroup, state),
          ),
        [],
      ),
    }))
    .filter((group) => group.rows.length > 0);
};

const flattenRows = (groups) =>
  groups.reduce((rows, group) => rows.concat(group.rows), []);

// "Sep 2021" from a sow date ('YYYY-MM-DD' or anything dayjs parses).
const formatSeasonMonth = (sowDate) => {
  if (!sowDate) {
    return '';
  }
  const parsed = dayjs(sowDate);
  return parsed.isValid() ? parsed.format('MMM YYYY') : '';
};

// Single-block field (or field named like its block): just the block name;
// otherwise "Field / Block". Archive adds " / <season month>".
const seasonTitle = (row, tab) => {
  if (!row) {
    return '';
  }
  const parts = shouldShowFieldHeader(row.fieldGroup)
    ? [row.fieldGroup.name, row.block.name]
    : [row.block.name];
  if (tab === SEASON_TABS.ARCHIVE) {
    const month = formatSeasonMonth(row.season?.crop_data?.sow_date);
    if (month) {
      parts.push(month);
    }
  }
  return parts.join(' / ');
};

const NO_IRRIGATION_SYSTEM = 'noIrrigationSystem';

// IrrigationSystem.jsx: the rate input's min / step (4 decimals for inch).
const irrigationRateMin = (lengthUnit) =>
  String(lengthUnit || '').toLowerCase() === 'inch' ? 0.0001 : 0.01;

const isBlank = (value) =>
  value === '' || value === null || value === undefined;

// A date on or after the season's harvest date (no harvest: fine).
const isNotBeforeHarvest = (date, harvestDate) =>
  !isBlank(date) &&
  date !== MULTIPLE_VALUES &&
  Boolean(harvestDate) &&
  harvestDate !== MULTIPLE_VALUES &&
  !dayjs(date).isBefore(dayjs(harvestDate));

// Kept for the sow date; the pre-season irrigation start has its own message.
const isSowNotBeforeHarvest = isNotBeforeHarvest;

const HARVEST_ERRORS = {
  sow_date: 'new_season_sow_after_harvest',
  irrigation_start_date: 'new_season_irrigation_start_after_harvest',
};

const harvestError = (key, harvestDate) => ({
  id: HARVEST_ERRORS[key],
  params: { harvest: harvestDate },
});

const sowAfterHarvestError = (harvestDate) =>
  harvestError('sow_date', harvestDate);

// The form's value for a crop date, or each season's own when left at
// Multiple values.
const seasonDate = (season, key, formValue) =>
  formValue === MULTIPLE_VALUES || formValue === undefined
    ? season?.crop_data?.[key]
    : formValue;

// The first selected season whose harvest date is not after the `key` date
// (sow_date, irrigation_start_date) it would be saved with, both taken from
// the form unless left at Multiple values. Returns that harvest date, or null.
const harvestConflictOf = (
  seasons,
  dateValue,
  harvestValue,
  key = 'sow_date',
) => {
  const conflict = (seasons || []).find((season) =>
    isNotBeforeHarvest(
      seasonDate(season, key, dateValue),
      seasonDate(season, 'harvest_date', harvestValue),
    ),
  );
  return conflict ? seasonDate(conflict, 'harvest_date', harvestValue) : null;
};

// {key: error} for the crop dates that are not before the harvest date, for
// the selected seasons with the form's values (Edit Field's KCBox only lets
// the sow and pre-season irrigation start dates be picked before harvest).
const harvestErrorsOf = (seasons, values) =>
  Object.keys(HARVEST_ERRORS).reduce((errors, key) => {
    const harvest = harvestConflictOf(
      seasons,
      values[key],
      values.harvest_date,
      key,
    );
    return harvest ? { ...errors, [key]: harvestError(key, harvest) } : errors;
  }, {});

// Edit Field's required inputs, as V1 enforces them: soil type, crop type and
// farming type (selects without an empty choice), sow date (required), the
// sow and pre-season irrigation start dates before the harvest date when
// there is one (KCBox shows an unset irrigation start as the sow date),
// irrigation type, and the irrigation rate (at least its step, unless
// there is no irrigation system). Values left at "Multiple
// values" in bulk edit are not checked. Returns {key: error}, an error being
// a translation id or {id, params}; `harvestDate` is the season's harvest
// date (the form's when it has one).
const validateSeasonValues = (values, { lengthUnit, harvestDate } = {}) => {
  const errors = {};
  const check = (key, isInvalid, error) => {
    const value = values[key];
    if (value !== MULTIPLE_VALUES && isInvalid(value)) {
      errors[key] = error;
    }
  };
  check('soil_type', isBlank, 'new_season_required');
  check('crop_type', isBlank, 'kc_crop_type_invalid_data_message');
  check('farming_type', isBlank, 'new_season_required');
  check('irrigation_type', isBlank, 'new_season_required');
  check('sow_date', isBlank, 'kc_sow_date_invalid_data_message');
  const harvest =
    values.harvest_date !== undefined && values.harvest_date !== MULTIPLE_VALUES
      ? values.harvest_date
      : harvestDate;
  Object.keys(HARVEST_ERRORS).forEach((key) => {
    if (!errors[key] && isNotBeforeHarvest(values[key], harvest)) {
      errors[key] = harvestError(key, harvest);
    }
  });

  if (values.irrigation_type !== NO_IRRIGATION_SYSTEM) {
    check(
      'irrigation_rate',
      (rate) =>
        isBlank(rate) ||
        isNaN(Number(rate)) ||
        Number(rate) < irrigationRateMin(lengthUnit),
      'new_season_irrigation_rate_invalid',
    );
  }
  return errors;
};

// Edit Field's required field size, once the user edits it (unset, the
// polygon's area is shown and nothing is sent).
const areaSizeErrors = (values, changed) =>
  changed.includes('area_size') &&
  (isBlank(values.area_size) || isNaN(Number(values.area_size)))
    ? { area_size: 'edit_field_field_size_invalid_data_message' }
    : {};

// Only the keys the user actually changed (and not left at "Multiple values").
const changedKeys = (initialValues, currentValues) =>
  FORM_KEYS.filter(
    (key) =>
      currentValues[key] !== MULTIPLE_VALUES &&
      currentValues[key] !== initialValues[key],
  );

const pick = (values, keys) =>
  keys.reduce((picked, key) => ({ ...picked, [key]: values[key] }), {});

// Advanced-section edits (Edit Field's KC table, planner, fertigation,
// meteorological stations): {path: value} on the parsed V1 Field, e.g.
// 'crop_data.kc_table' or 'settings.planner_settings'. Never in FORM_KEYS.
const isFieldPath = (key) =>
  key.includes('.') || key === 'initial_concentrations';

// The V1 request keys a Field path is sent under.
const bodyKeysOfPath = (path) => {
  switch (path.split('.')[0]) {
    case 'initial_concentrations':
      return ['soil_data'];
    case 'contract_data':
      return ['installation_date', 'expiration_date'];
    default:
      return [path.split('.')[0]];
  }
};

// Sets `value` at a dotted path, copying each object on the way (the Field's
// nested objects may be shared with the stored season).
const setPath = (target, path, value) => {
  const [head, ...rest] = path.split('.');
  if (rest.length === 0) {
    target[head] = value;
    return;
  }
  target[head] = { ...(target[head] || {}) };
  setPath(target[head], rest.join('.'), value);
};

// A template's PUT body: only what changed, merged onto the template's own
// nested objects (the API writes crop_data / irrigation_system as sent).
// `defaultRecommendationDisplay` is fieldManager's, passed in to stay pure.
// Field-path edits are taken from `v1Body` (the V1 body of the template with
// every change applied), under the request keys they belong to.
const buildTemplateBody = (
  season,
  changes,
  defaultRecommendationDisplay,
  v1Body,
) => {
  const body = {};
  const keys = Object.keys(changes);
  ['soil_type', 'farming_type', 'expiration_date'].forEach((key) => {
    if (keys.includes(key)) {
      body[key] = changes[key];
    }
  });
  if (keys.includes('cluster')) {
    body.cluster = { ...(season.cluster || {}), id: changes.cluster };
  }
  if (keys.includes('area_size')) {
    body.geo_data = {
      ...(season.geo_data || {}),
      area_size: changes.area_size,
    };
  }
  const settingsKeys = keys.filter((key) => SETTINGS_KEYS.includes(key));
  if (settingsKeys.length) {
    body.settings = {
      ...(season.settings || {}),
      ...pick(changes, settingsKeys),
    };
  }
  const cropKeys = keys.filter((key) => CROP_KEYS.includes(key));
  if (cropKeys.length) {
    body.crop_data = {
      ...(season.crop_data || {}),
      ...pick(changes, cropKeys),
    };
  }
  const irrigationKeys = keys.filter((key) => IRRIGATION_KEYS.includes(key));
  if (irrigationKeys.length) {
    const reset = keys.includes('irrigation_type')
      ? {
          recommendation_display: defaultRecommendationDisplay(
            changes.irrigation_type,
          ),
        }
      : {};
    body.irrigation_system = {
      ...(season.irrigation_system || {}),
      ...reset,
      ...pick(changes, irrigationKeys),
    };
  }
  keys.filter(isFieldPath).forEach((path) => {
    bodyKeysOfPath(path).forEach((bodyKey) => {
      if (v1Body && bodyKey in v1Body) {
        body[bodyKey] = v1Body[bodyKey];
      }
    });
  });
  return body;
};

const SENSORS_MISSING_ERROR = 'season_sensors_missing';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const sameKeys = (a, b) => {
  const left = Object.keys(a).sort();
  const right = Object.keys(b).sort();
  return (
    left.length === right.length &&
    left.every((key, index) => key === right[index])
  );
};

// The PUT v2/seasons body for one season, built from its DETAIL
// (GET v2/seasons/...), never from the tree (which has no sensors).
// - template: partial body, never any `sensors` (the API rejects them).
// - otherwise: deps.buildV1Body(detailSeason, changes, sensors) — the exact V1
//   body — and it must carry exactly the sensors meant to be saved: `sensors`
//   (the user's sensor edits, keyed by id) when given, else the detail's. V1's
//   update deactivates every sensor missing from the request, so a body that
//   lost one is never returned; a sensor the user deleted is simply not in
//   `sensors`.
const buildSeasonPutBody = (detailSeason, changes, deps, sensors) => {
  if (detailSeason?.state === 'template') {
    const hasFieldPaths = Object.keys(changes).some(isFieldPath);
    return buildTemplateBody(
      detailSeason,
      changes,
      deps.defaultRecommendationDisplay,
      hasFieldPaths ? deps.buildV1Body(detailSeason, changes, {}) : undefined,
    );
  }
  if (!isPlainObject(detailSeason?.sensors)) {
    throw new Error(SENSORS_MISSING_ERROR);
  }
  const body = deps.buildV1Body(detailSeason, changes, sensors);
  if (
    !isPlainObject(body?.sensors) ||
    !sameKeys(body.sensors, sensors || detailSeason.sensors)
  ) {
    throw new Error(SENSORS_MISSING_ERROR);
  }
  return body;
};

module.exports = {
  NO_IRRIGATION_SYSTEM,
  irrigationRateMin,
  validateSeasonValues,
  harvestConflictOf,
  harvestErrorsOf,
  areaSizeErrors,
  sowAfterHarvestError,
  isSowNotBeforeHarvest,
  isFieldPath,
  setPath,
  SETTINGS_KEYS,
  preSeasonSourceOf,
  activationContractDates,
  SENSORS_MISSING_ERROR,
  buildSeasonPutBody,
  SEASON_TABS,
  TAB_STATE,
  MULTIPLE_VALUES,
  FORM_KEYS,
  CROP_KEYS,
  IRRIGATION_KEYS,
  readSeasonValue,
  commonValues,
  shouldShowFieldHeader,
  buildSeasonGroups,
  flattenRows,
  formatSeasonMonth,
  seasonTitle,
  changedKeys,
  pick,
  buildTemplateBody,
};
